import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip — portal-based tooltip that renders outside overflow contexts.
 * Appears to the right by default, or specify placement.
 *
 * Usage: <Tooltip text="Label"><button>...</button></Tooltip>
 */
export default function Tooltip({ text, children, placement = 'right' }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (visible && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      if (placement === 'right') {
        setPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
      } else if (placement === 'bottom') {
        setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
      } else if (placement === 'left') {
        setPos({ top: rect.top + rect.height / 2, left: rect.left - 8 });
      }
    }
  }, [visible, placement]);

  if (!text) return children;

  const arrowClass = placement === 'right'
    ? 'absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[4px] w-0 h-0 border-y-4 border-y-transparent border-r-4 border-r-gray-800'
    : placement === 'bottom'
    ? 'absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[4px] w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800'
    : 'absolute right-0 top-1/2 -translate-y-1/2 translate-x-[4px] w-0 h-0 border-y-4 border-y-transparent border-l-4 border-l-gray-800';

  const transform = placement === 'right'
    ? 'translateY(-50%)'
    : placement === 'bottom'
    ? 'translateX(-50%)'
    : 'translateY(-50%)';

  return (
    <div
      ref={ref}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="inline-flex"
    >
      {children}
      {visible && createPortal(
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{ top: pos.top, left: pos.left, transform, opacity: 1, transition: 'opacity 100ms ease' }}
        >
          <div className={arrowClass} />
          <div className="bg-gray-800 text-white text-[11px] font-medium rounded px-2 py-1 whitespace-nowrap shadow-lg">
            {text}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
